import * as React from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/nextjs';
import * as cookie from 'cookie';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useRouter } from 'next/router';
import { AuthStatus, SocialAuthProvider } from 'constants/auth';
import { FORGOT_PASSWORD_PAGE, HOME_PAGE, SIGNUP_CODE_PAGE } from 'constants/pages';
import { ErrorResponse, RequestStatus } from 'constants/requests';
import { CoachStatusEnum } from 'types/generated/server';
import { auth } from 'services/client/firebase';
import { getIsNativePlatform } from 'utils/mobile/getIsNativePlatform';
import { detectFraudFromBrowser } from 'utils/shared/fraud/detectFraud';
import { getSafeRedirectUrl } from 'utils/shared/getSafeRedirectUrl';
import { sleep } from 'utils/shared/sleep';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useOAuthAuthentication } from 'hooks/useOAuthAuthentication';
import { useViewer } from 'hooks/useViewer';
import LogoWithName from 'svg/LogoWithName';
import Button, { ButtonText } from 'components/Button';
import Link from 'components/Link';
import CardError from 'components/cards/CardError';
import OAuthButtons from 'components/forms/OAuthButtons';
import ModalAccountLinking from 'components/modals/ModalAccountLinking';
import {
  CLUB_DASHBOARD_PROMPT_CHECK_EVENT,
  SHOW_CLUB_DASHBOARD_PROMPT_STORAGE_KEY,
} from 'components/modals/ModalClubDashboardPrompt';
import classNames from 'styles/utils/classNames';

interface Props {
  isShowSignupLink?: boolean;
  loginSuccessUrl?: string;
  setIsCloseBlocked?: (isCloseBlocked: boolean) => void;
  toggleSignup?: () => void;
  isFormOnly?: boolean;
  formWrapperClassName?: string;
  buttonWrapperClassName?: string;
  spacerClassName?: string;
  shouldSkipReload?: boolean;
  handleAuthComplete?: () => void;
}

export default function FormLogin({
  loginSuccessUrl,
  setIsCloseBlocked = () => {},
  isShowSignupLink = false,
  toggleSignup,
  isFormOnly,
  formWrapperClassName,
  buttonWrapperClassName,
  spacerClassName,
  shouldSkipReload,
  handleAuthComplete,
}: Props) {
  const router = useRouter();
  const viewer = useViewer();
  const { user } = useGetCurrentUser();
  const {
    authenticateWithProvider,
    isLoading: isOAuthLoading,
    error: oauthError,
    linking,
    linkingError,
    isLinking,
    linkWithExistingProvider,
    linkWithPassword,
    cancelLinking,
  } = useOAuthAuthentication();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [isShowPassword, setIsShowPassword] = React.useState(false);
  const [error, setError] = React.useState<ErrorResponse | null>(null);
  const [requestStatus, setRequestStatus] = React.useState(RequestStatus.Idle);
  const isDisabled =
    requestStatus === RequestStatus.InProgress ||
    requestStatus === RequestStatus.Success ||
    viewer.status === AuthStatus.User ||
    isOAuthLoading;
  const isHidden =
    !getIsNativePlatform() &&
    (viewer.status === AuthStatus.Loading || viewer.status === AuthStatus.User) &&
    !error &&
    requestStatus !== RequestStatus.InProgress &&
    requestStatus !== RequestStatus.Success;

  React.useEffect(() => {
    if (router.query.email && typeof router.query.email === 'string') {
      setEmail(decodeURIComponent(router.query.email));
    }
  }, [router.isReady]);

  React.useEffect(() => {
    if (user?.id) {
      const coachStatusCookie = cookie.serialize(
        'accountType',
        user?.coachStatus === CoachStatusEnum.Active ? 'COACH' : 'PLAYER',
        {
          maxAge: 60 * 60 * 24 * 100, // 100 week
          path: '/',
          secure: process.env.NODE_ENV === 'production', // Ensures cookie is only sent over HTTPS in production
          sameSite: 'strict', // CSRF protection
        },
      );

      // Set the cookie using document.cookie
      document.cookie = coachStatusCookie;
    }
  }, [user?.coachStatus]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = React.useCallback(
    async (e) => {
      e.preventDefault();

      if (requestStatus === RequestStatus.InProgress || requestStatus === RequestStatus.Success) {
        return;
      }

      // Check for fraud before proceeding
      const fraudDetection = detectFraudFromBrowser();
      if (fraudDetection.isFraud) {
        Sentry.captureException(new Error('Fraud attempt detected during login'), {
          extra: {
            reason: fraudDetection.reason,
            timezone: fraudDetection.timezone,
            locale: fraudDetection.locale,
            email,
          },
        });
        setError({ message: 'Unable to log in. Please try again later.' });
        setRequestStatus(RequestStatus.Error);
        setIsCloseBlocked(false);
        return;
      }

      setIsCloseBlocked(true);
      setRequestStatus(RequestStatus.InProgress);
      setError(null);

      // Fallback to DOM values for mobile password manager autofill
      let loginEmail = email;
      let loginPassword = password;
      if (!loginEmail || !loginPassword) {
        const formData = new FormData(e.currentTarget);
        if (!loginEmail) loginEmail = (formData.get('email') as string) || '';
        if (!loginPassword) loginPassword = (formData.get('current-password') as string) || '';
      }

      signInWithEmailAndPassword(auth, loginEmail, loginPassword)
        .then(function (user) {
          setRequestStatus(RequestStatus.Success);
          return user;
        })
        .then(async () => {
          const loggedInUser = auth.currentUser;
          await loggedInUser?.getIdToken(true);

          // NOTE: Giving time for the current user query to run since it's triggered in the useEffect in src/context/CurrentUserContext.
          await sleep(1000);
          setIsCloseBlocked(false);

          sessionStorage.setItem(SHOW_CLUB_DASHBOARD_PROMPT_STORAGE_KEY, 'true');

          if (loginSuccessUrl) {
            const redirectUrl = getSafeRedirectUrl(router.query.next, loginSuccessUrl);

            router.push(redirectUrl);
          } else if (!shouldSkipReload) {
            router.reload();
          } else {
            // No navigation happens on this path, so nothing else re-triggers the club prompt check.
            window.dispatchEvent(new Event(CLUB_DASHBOARD_PROMPT_CHECK_EVENT));
            handleAuthComplete?.();
          }
        })
        .catch(function (errorResponse) {
          Sentry.captureException(errorResponse);
          setRequestStatus(RequestStatus.Error);
          setIsCloseBlocked(false);
          setError(errorResponse.code ? new Error(errorResponse.code) : errorResponse);
        });
    },
    [email, password, requestStatus],
  );

  // Mirrors the email-login success redirect; used when an existing user signs in via OAuth.
  const redirectAfterLogin = () => {
    setIsCloseBlocked(false);
    sessionStorage.setItem(SHOW_CLUB_DASHBOARD_PROMPT_STORAGE_KEY, 'true');

    if (loginSuccessUrl) {
      const redirectUrl = getSafeRedirectUrl(router.query.next, loginSuccessUrl);
      router.push(redirectUrl);
    } else if (!shouldSkipReload) {
      router.reload();
    } else {
      window.dispatchEvent(new Event(CLUB_DASHBOARD_PROMPT_CHECK_EVENT));
      handleAuthComplete?.();
    }
  };

  const handleProviderSelect = (provider: SocialAuthProvider) => {
    setIsCloseBlocked(true);
    authenticateWithProvider(provider, {
      onExistingUser: redirectAfterLogin,
      returnTo: loginSuccessUrl
        ? getSafeRedirectUrl(router.query.next, loginSuccessUrl)
        : router.asPath,
    });
  };

  return (
    <>
      <form
        className="flex h-full w-full grow flex-col items-center lg:justify-center"
        onSubmit={handleSubmit}
      >
        <div className="flex h-full w-full grow flex-col items-center overflow-y-auto lg:h-auto lg:max-w-[560px] lg:grow-0">
          {!isFormOnly && (
            <h1 className="flex shrink-0 items-center justify-center space-x-4 px-6 pt-12">
              <span className="text-2xl font-semibold text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                <LogoWithName className="h-6" />
              </span>
            </h1>
          )}
          <div
            className={classNames(
              'flex w-full flex-auto flex-col items-center justify-center transition-opacity duration-700',
              !isFormOnly && 'px-6 py-4',
              !!formWrapperClassName && formWrapperClassName,
              isHidden ? 'opacity-0' : 'opacity-100',
            )}
          >
            {!isFormOnly && <h2 className="font mb-6 text-xl lg:mt-10">Welcome back!</h2>}
            <div
              className={classNames(
                'flex w-full flex-col',
                spacerClassName ? spacerClassName : 'space-y-8',
              )}
            >
              <div className="w-full">
                <label className="sr-only" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  disabled={isDisabled}
                  className="input-form"
                  required
                />
              </div>
              <div className="w-full">
                <label className="sr-only" htmlFor="password">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    autoComplete="current-password"
                    name="current-password"
                    type={isShowPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    disabled={isDisabled}
                    className="input-form ph-no-autocapture"
                    required
                  />
                  <div className="absolute right-4 top-3 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    {isShowPassword ? (
                      <button
                        className="h-4 w-4"
                        type="button"
                        onClick={() => setIsShowPassword(false)}
                      >
                        <EyeIcon />
                      </button>
                    ) : (
                      <button type="button" onClick={() => setIsShowPassword(true)}>
                        <EyeSlashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-6 text-right">
                  {isDisabled ? (
                    <span className="font-medium text-color-brand-highlight opacity-50">
                      Forgot password?
                    </span>
                  ) : (
                    <Link
                      href={`${FORGOT_PASSWORD_PAGE}${
                        !!email ? `?email=${encodeURIComponent(email)}` : ''
                      }`}
                      className="font-medium text-color-brand-highlight"
                    >
                      Forgot password?
                    </Link>
                  )}
                </div>
              </div>
            </div>
            {isShowSignupLink && (
              <div className="mt-16 text-center lg:mt-20">
                <div className="mb-2 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  Don't have an account yet?
                </div>
                {isDisabled ? (
                  <span className="text-sm font-medium leading-4 text-color-brand-highlight opacity-50">
                    Create an account
                  </span>
                ) : toggleSignup ? (
                  <ButtonText
                    type="button"
                    className="font-medium leading-4 text-color-brand-highlight"
                    onClick={() => toggleSignup()}
                  >
                    Create an account
                  </ButtonText>
                ) : (
                  <Link
                    href={SIGNUP_CODE_PAGE}
                    className="text-sm font-medium leading-4 text-color-brand-highlight"
                  >
                    Create an account
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
        <div
          className={classNames(
            'w-full shrink-0 transition-opacity duration-700 lg:max-w-[560px]',
            !isFormOnly && 'px-4 pb-16 sm:px-[6.25rem]',
            !!buttonWrapperClassName && buttonWrapperClassName,
            isHidden ? 'opacity-0' : 'opacity-100',
          )}
        >
          {!!(error?.message || oauthError) && (
            <div className="mb-4">
              <CardError>{error?.message || oauthError?.message}</CardError>
            </div>
          )}
          <Button type="submit" variant="primary" size="lg" loading={isDisabled}>
            {isDisabled ? 'Logging in...' : 'Log in'}
          </Button>
          <div className="my-4 flex items-center gap-3">
            <span className="h-px flex-1 bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
            <span className="typography-product-caption text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
              or
            </span>
            <span className="h-px flex-1 bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
          </div>
          {/* The submit button is deliberately narrow; the provider row spans the field width. */}
          <div className={classNames(!isFormOnly && 'sm:-mx-[4.75rem]')}>
            <OAuthButtons onSelect={handleProviderSelect} disabled={isDisabled} />
          </div>
        </div>
      </form>
      {linking && (
        <ModalAccountLinking
          email={linking.email}
          newProvider={linking.newProvider}
          existingMethods={linking.existingMethods}
          isLinking={isLinking}
          error={linkingError}
          onLinkWithExistingProvider={linkWithExistingProvider}
          onLinkWithPassword={linkWithPassword}
          onCancel={cancelLinking}
        />
      )}
    </>
  );
}
